import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { GoalIcon } from '@repo/ui/icons'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { confirmMissions } from '../../api/missions/confirm-mission'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { getAffectedUserBasedOnType } from '../../components/pages/mission/affected-lists/utils'
import MissionStaffBadge from '../../components/pages/mission/mission-staff-badge/mission-staff-badge'
import { MissionsFilter } from '../../components/pages/mission/missions-filters'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { missionStatusEnumMapping } from '../../lib/types/enums/mission/mission-status.enum'
import {
  MissionTypeEnum,
  missionTypeEnumMapping
} from '../../lib/types/enums/mission/mission-type.enum'
import { staffQualificationEnumMapping } from '../../lib/types/enums/staff/staff-qualifications.enum'
import { tripStepTypeMapping } from '../../lib/types/enums/trip/trip-type.enum'
import { Mission } from '../../lib/types/models/mission/mission'
import { MissionFilter } from '../../lib/types/models/mission/mission-filter'
import { MissionProperties } from '../../lib/types/models/mission/properties.mission'
import {
  transformMissionFiltersToUrl,
  transformUrlToMissionFilters
} from '../../lib/utils/filters-mapping/mission'

export const missionColumns = (): Column<Mission>[] => [
  {
    accessorKey: 'status',
    header: 'Statut',
    enableSorting: true,
    cell: ({ row }) => (
      <Badge
        text={missionStatusEnumMapping[row.original.status].label}
        variant={missionStatusEnumMapping[row.original.status].variant}
      />
    )
  },
  {
    accessorKey: 'directionality',
    header: 'Type de trajet',
    enableSorting: true,
    cell: ({ row }) =>
      row.original.directionality ? (
        <Badge
          text={tripStepTypeMapping[row.original.directionality].label}
          variant={tripStepTypeMapping[row.original.directionality].variant}
        />
      ) : (
        '-'
      )
  },
  {
    accessorKey: 'date',
    header: 'Date',
    enableSorting: true,
    cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.date)} />
  },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: true,
    cell: ({ row }) => missionTypeEnumMapping[row.original.type].label
  },
  {
    accessorKey: 'missionType.title',
    header: 'Libellé',
    enableSorting: true,
    cell: ({ row }) => {
      const qualification = row.original.qualifications?.[0]

      if (!qualification) return row.original.missionType.title

      const { color } = staffQualificationEnumMapping[qualification]

      return (
        <Badge
          text={row.original.missionType.title}
          color={color.background}
          textColor={color.text}
        />
      )
    }
  },
  {
    accessorKey: 'appointmentTime',
    header: 'Heure de RDV Mission',
    enableSorting: true,
    cell: ({ row }) =>
      row.original.appointmentTime ? (
        <Badge text={DateFormatter.getFormattedTime(row.original.appointmentTime)} />
      ) : (
        '-'
      )
  },
  {
    accessorKey: 'departureTimes.time',
    header: 'Heures de départ',
    cell: ({ row }) => {
      const departureTimes = MissionProperties.getDepartureTimes(row.original)
      if (departureTimes.length === 0) return '-'
      return departureTimes.map((departureTime) => (
        <Badge
          key={departureTime.id}
          text={DateFormatter.getFormattedTime(departureTime.time)}
          className="my-1"
        />
      ))
    }
  },
  {
    accessorKey: 'arrivalTimes',
    header: "Heures d'arrivée",
    cell: ({ row }) => {
      const arrivalTimes = MissionProperties.getArrivalTimes(row.original)

      if (arrivalTimes.length === 0) return '-'
      return arrivalTimes.map((arrivalTime) => (
        <Badge
          key={arrivalTime.id}
          text={DateFormatter.getFormattedTime(arrivalTime.time)}
          className="my-1"
        />
      ))
    }
  },

  {
    accessorKey: 'meetingPlace.label',
    header: 'Lieu de RDV',
    enableSorting: true,
    cell: ({ row }) => row.original.meetingPlace.label
  },
  {
    accessorKey: 'endPlace.label',
    header: 'Lieu de fin de mission',
    enableSorting: true,
    cell: ({ row }) => row.original.endPlace?.label ?? '-'
  },
  {
    accessorKey: 'requiredNumberOfStaff',
    header: 'Effectifs mission',
    enableSorting: true,
    cell: ({ row }) => row.original.requiredNumberOfStaff ?? '-'
  },
  {
    accessorKey: '',
    header: 'Staff',
    cell: ({ row }) => {
      const staffs = getAffectedUserBasedOnType(row.original)
      return (
        <div className="flex flex-wrap gap-2">
          {staffs.map((staff) => {
            return <MissionStaffBadge key={staff.id} affectedUser={staff} mission={row.original} />
          })}
        </div>
      )
    }
  }
]

const MissionListPage = () => {
  const [isTableLoading, setIsTableLoading] = useState(false)

  const tableRef = useRef<DynamicTableRef<Mission>>(null)
  const { navigateWithEvent } = useNavigator()

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<MissionFilter>({
      initialFilters: {},
      options: { allowSearchParam: true },
      transformFilterToUrl: transformMissionFiltersToUrl,
      transformUrlToFilter: transformUrlToMissionFilters
    })

  const { mutateAsync } = useMutation({
    mutationFn: confirmMissions,
    onSuccess: () => tableRef.current?.refetch()
  })

  return (
    <div>
      <UserNavBar title={'Liste des missions'} icon={GoalIcon} />
      <div className="h-4" />
      <DynamicDataTable<Mission, MissionFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        title={'Toutes les missions'}
        columns={missionColumns()}
        deleteMessage={{ name: 'la mission', attributeName: 'title' }}
        selectable
        allowSearchParam
        onRowClick={(mission, event) =>
          navigateWithEvent(Paths.MISSION_VIEW_PAGE.replace(':id', mission.id.toString()), event)
        }
        onFiltersClick={() => toggleModal('filters')}
        endpoint={API.missions.getAll.url}
        actions={[
          {
            label: `Confirmer l'affectation`,
            type: 'multiple',
            fn: async (missions) => {
              const missionWithNoStaff = missions.find((mission) => {
                return (
                  (mission.type === MissionTypeEnum.STAFF &&
                    mission.affectedStaffs.length === 0) ||
                  (mission.type === MissionTypeEnum.OFFICE &&
                    mission.affectedLecStaffs.length === 0) ||
                  (mission.type === MissionTypeEnum.EXTERNAL &&
                    mission.affectedExternalStaffs.length === 0)
                )
              })
              if (missionWithNoStaff) {
                toast.error(
                  `La mission "${missionWithNoStaff.missionType.title}" n'a pas de staff affecté. Veuillez affecter au moins un staff avant de confirmer l'affectation.`
                )
                return
              }
              await mutateAsync(missions.map((mission) => mission.id))
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <MissionsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default MissionListPage
